// @ts-nocheck
import { useState, useMemo, useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Input, Table, Progress, Avatar, Select } from "../components/admin-ui";
import {
  DownloadOutlined, EyeOutlined, SearchOutlined,
  ShoppingOutlined, CarOutlined, ArrowUpOutlined,
  TeamOutlined, ClockCircleOutlined, LoginOutlined,
} from "../components/admin-icons";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import type { ColumnsType } from "../components/admin-ui";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import type { Transaction } from "@kawkaw/shared";
import { payMethodLabel, cancelReasonLabel, formatMYR } from "@kawkaw/shared";

function exportRowsToCsv(filename: string, rows: Record<string, unknown>[]) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csvRows = rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","));
  const csv = [headers.join(","), ...csvRows].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}-${formatMytStamp(new Date()).slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
import { AdminShell } from "../layouts/AdminShell";
import { SettlementTab } from "./ReportsSettlement";
import {
  useRevenueReport, useStallBreakdown, useDashboardStats, useAdminTransactions,
  useHighRiskOrders, useTopItemsByStall, useAdminStalls, usePricingBreakdown, useUpdateStall,
  usePaymentMethodsReport, useStaffSessionsReport, useAdminStaff,
} from "../api/hooks";
import { tokens } from "../styles/tokens";
import { formatDateOnly, formatMonthDay, formatOrderTime, formatMytStamp } from "../utils/formatLocaleDate";
const { color: T } = tokens;

/* ─── Types ──────────────────────────────────────────────── */
type Period = "today" | "week" | "month";
type ReportTab = "financial" | "risk" | "stalls" | "settlement" | "staff" | "pricing";

/* ─── Staff Activity helpers (real StaffSession data via useStaffSessionsReport) ── */
type StaffSessionRow = {
  id: string;
  staffId: string;
  name: string;
  initials: string;
  color: string;
  device: string;
  loginAt: string;
  logoutAt: string | null;
  loginTime: string;
  logoutTime: string | null;
  durationMinutes: number | null;
  ongoing: boolean;
  status: "online" | "completed";
};

function getStaffInitials(name: string): string {
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? ((parts[0]![0] ?? "") + (parts[parts.length - 1]![0] ?? "")).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}
const STAFF_AVATAR_COLORS = [T.brand, T.successBright, T.infoText, T.purpleMid, T.avatarPink, T.warningText, T.avatarCyan];
function getStaffAvatarColor(name: string): string {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0xffff;
  return STAFF_AVATAR_COLORS[h % STAFF_AVATAR_COLORS.length] ?? T.brand;
}
function formatSessionDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}
const DONUT_COLORS = [T.brand, T.brandChartA, T.brandChartB, T.warningBg];

/* ─── Period helpers ─────────────────────────────────────── */
function periodRange(period: Period): [Dayjs, Dayjs] {
  const today = dayjs();
  if (period === "today") return [today.startOf("day"), today.endOf("day")];
  if (period === "week")  return [today.subtract(6, "day").startOf("day"), today.endOf("day")];
  return [today.startOf("month"), today.endOf("day")];
}

function periodLabel(range: [Dayjs, Dayjs], lng: string): string {
  return `${formatDateOnly(range[0], lng)} – ${formatDateOnly(range[1], lng)}`;
}

/* ─── Shared sub-components ──────────────────────────────── */
function TabBar({ active, onChange }: { active: ReportTab; onChange: (tab: ReportTab) => void }) {
  const { t, i18n } = useTranslation();
  const tabs = useMemo(() => [
    { key: "financial" as const, label: t("reports.tabFinancial") },
    { key: "risk" as const,      label: t("reports.tabRisk") },
    { key: "stalls" as const,    label: t("reports.tabStalls") },
    { key: "settlement" as const, label: t("reports.tabSettlement") },
    { key: "staff" as const,     label: t("reports.tabStaff") },
    { key: "pricing" as const,   label: t("reports.tabPricing") },
  ], [t]);
  return (
    <div className="admin-reports-tabs" role="tablist">
      {tabs.map(tab => (
        <button key={tab.key} type="button" role="tab" aria-selected={active === tab.key} onClick={() => onChange(tab.key)} style={{
          padding: "10px 20px", border: "none", background: "transparent", cursor: "pointer",
          fontSize: 14, fontWeight: active === tab.key ? 700 : 500,
          color: active === tab.key ? T.brand : T.textMuted,
          borderBottom: active === tab.key ? "2px solid #F97316" : "2px solid transparent",
          marginBottom: -2, transition: "all .15s", flexShrink: 0,
        }}>
          {tab.label}
        </button>
      ))}
    </div>
  );
}

function PeriodBar({ period, onChange, range }: { period: Period; onChange: (p: Period) => void; range: [Dayjs, Dayjs] }) {
  const { t, i18n } = useTranslation();
  const btns = useMemo(() => [
    { key: "today" as const, label: t("reports.periodToday") },
    { key: "week" as const,  label: t("reports.periodWeek") },
    { key: "month" as const, label: t("reports.periodMonth") },
  ], [t]);
  return (
    <div className="admin-reports-period admin-txn-filter-panel">
      <div className="admin-reports-period-options">
        {btns.map(b => (
          <button key={b.key} type="button" aria-pressed={period === b.key} onClick={() => onChange(b.key)} style={{
            padding: "8px 18px", borderRadius: 8,
            border: period === b.key ? "2px solid #212121" : "1px solid #E5E7EB",
            background: period === b.key ? T.textDark : T.surface,
            color: period === b.key ? T.surface : T.textBody,
            fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "all .15s",
          }}>
            {b.label}
          </button>
        ))}
      </div>
      <span className="admin-reports-period-label" style={{ fontSize: 12, color: T.textBody, display: "flex", alignItems: "center", gap: 5 }}>
        📅 {periodLabel(range, i18n.language)}
      </span>
    </div>
  );
}

function SectionCard({ children, style, className }: { children: ReactNode; style?: React.CSSProperties; className?: string }) {
  return (
    <div className={`admin-report-section-card admin-data-table-panel ${className ?? ""}`.trim()} style={{
      background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB",
      padding: "20px 24px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)", ...style,
    }}>
      {children}
    </div>
  );
}

function SectionTitle({ children, right }: { children: ReactNode; right?: ReactNode }) {
  return (
    <div className="admin-report-section-heading" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
      <span style={{ fontSize: 16, fontWeight: 800, color: T.textDark }}>{children}</span>
      {right}
    </div>
  );
}

function ExportBtn({ onClick }: { onClick?: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      size="small" icon={<DownloadOutlined />} onClick={onClick}
      className="admin-report-section-export"
      style={{ height: 32, borderRadius: 8, fontWeight: 700, fontSize: 12, color: T.brand, borderColor: T.brand }}
    >
      {t("common.exportCsv")}
    </Button>
  );
}

/* ─── Financial Tab ──────────────────────────────────────── */
function FinancialTab({ params, period }: { params: { from: string; to: string; granularity: string }; period: Period }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: revenueData, isLoading: revLoading } = useRevenueReport(params);
  const { data: dashStats } = useDashboardStats();
  const { data: txnData, isLoading: txnLoading } = useAdminTransactions({ limit: 5, page: 1 });
  const { data: paymentMethodsData, isLoading: pmLoading } = usePaymentMethodsReport({ from: params.from, to: params.to });

  const PAY_METHOD_LABEL: Record<string, string> = {
    cash: t("refunds.payCash"),
    fpx: t("refunds.payFpx"),
    visa: t("refunds.payVisa"),
    grabpay: t("refunds.payGrab"),
    tng: t("refunds.payTng"),
    boost: t("refunds.payBoost"),
    ewallet: t("refunds.payEwallet"),
  };

  const chartRows = (revenueData?.data ?? []).map(d => ({
    label: d.label,
    revenue: parseFloat(d.revenue),
    orders:  d.orders,
    refunds: parseFloat(d.refunds ?? "0"),
    refundCount: d.refundCount ?? 0,
    net: parseFloat(d.netRevenue ?? d.revenue),
    cancelledRefunds: parseFloat(d.cancelledRefunds ?? "0"),
    cancelledRefundCount: d.cancelledRefundCount ?? 0,
  }));

  const totalRevenue  = chartRows.reduce((s, r) => s + r.revenue, 0);
  // Server aggregates over the same range as the revenue rows (never a placeholder).
  const totalRefunds     = chartRows.reduce((s, r) => s + r.refunds, 0);
  const totalRefundCount = chartRows.reduce((s, r) => s + r.refundCount, 0);
  const totalNet         = chartRows.reduce((s, r) => s + r.net, 0);
  const totalCancelledRefunds     = chartRows.reduce((s, r) => s + r.cancelledRefunds, 0);
  const totalCancelledRefundCount = chartRows.reduce((s, r) => s + r.cancelledRefundCount, 0);
  const totalOrders   = chartRows.reduce((s, r) => s + r.orders,  0);
  const revChange     = dashStats?.data?.revChangePercent ?? null;

  const STATUS_PILL: Record<string, { color: string; bg: string }> = {
    completed: { color: T.successText, bg: T.successBg },
    preparing: { color: T.brandAmberText, bg: T.warningBg },
    pending:   { color: T.infoDeep, bg: T.infoBg },
    cancelled: { color: T.dangerText, bg: T.dangerBg },
    refunded:  { color: T.textMuted, bg: T.surfaceSection },
  };

  const txnColumns: ColumnsType<Transaction> = useMemo(() => [
    {
      title: t("reports.colTxnId").toUpperCase(), dataIndex: "reference", key: "ref", width: 120,
      render: (v: string | null, row: Transaction) => (
        <span style={{ fontFamily: "monospace", fontWeight: 700, color: T.brand, fontSize: 12 }}>
          #{v ?? row.id.slice(0, 8).toUpperCase()}
        </span>
      ),
    },
    {
      title: t("reports.colStall").toUpperCase(), key: "stall", width: 160,
      render: (_: unknown, row: Transaction) => {
        const items = (row as unknown as { order?: { items?: Array<{ stall?: { name?: string } }> } }).order?.items ?? [];
        const stallNames = Array.from(new Set(items.map((i) => i.stall?.name).filter(Boolean)));
        return (
          <span style={{ fontSize: 13, color: T.textDark }}>
            {stallNames.length > 0 ? stallNames.join(", ") : "—"}
          </span>
        );
      },
    },
    {
      title: t("reports.colStatus").toUpperCase(), dataIndex: "status", key: "status", width: 120,
      render: (v: string) => {
        const s = STATUS_PILL[v] ?? { color: T.siderHover, bg: T.surfaceSection };
        const TXN_STATUS_KEY: Record<string, string> = { success: "statusSuccess", pending: "statusPending", partial_refund: "statusPartialRefund", refunded: "statusRefunded", failed: "statusFailed" };
        return (
          <span style={{ padding: "3px 10px", borderRadius: 100, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, textTransform: "uppercase" as const }}>
            {TXN_STATUS_KEY[v] ? t(`transactions.${TXN_STATUS_KEY[v]}`) : v}
          </span>
        );
      },
    },
    {
      title: t("reports.colMethod").toUpperCase(), dataIndex: "method", key: "method", width: 110,
      render: (v: string) => <span style={{ fontSize: 13, color: T.textBody }}>{payMethodLabel(v, i18n.language) ?? PAY_METHOD_LABEL[v] ?? v}</span>,
    },
    {
      title: t("reports.colAmount").toUpperCase(), dataIndex: "amount", key: "amount", width: 110, align: "right",
      render: (v: string) => (
        <span style={{ fontWeight: 700, color: T.textDark }}>{formatMYR(parseFloat(v))}</span>
      ),
    },
  ], [t]);

  return (
    <div className="admin-report-tab admin-report-financial-tab">
      {/* Stat cards */}
      <div className="admin-report-kpis" style={{ display: "flex", gap: 14, marginBottom: 18, flexWrap: "wrap" }}>
        {/* Total Revenue */}
        <div title={t("reports.cardTotalRevenueHint")} style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB", borderTop: "3px solid #F97316", padding: "18px 20px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 8 }}>{t("reports.cardTotalRevenue").toUpperCase()}</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: T.textDark }}>
            {formatMYR(period === "today" && dashStats?.data ? parseFloat(dashStats.data.todayRevenue) : totalRevenue)}
          </div>
          {revChange !== null && (
            <div style={{ fontSize: 12, color: T.successBright, marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
              <ArrowUpOutlined style={{ fontSize: 10 }} />
              {t("reports.revVsLastPeriod", { pct: Math.abs(revChange).toFixed(0) })}
            </div>
          )}
        </div>
        {/* Total Refunds */}
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB", padding: "18px 20px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 8 }}>{t("reports.cardTotalRefunds").toUpperCase()}</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: T.textDark }}>{formatMYR(totalRefunds)}</div>
          <div style={{ fontSize: 12, color: T.dangerText, marginTop: 4 }}>{t("reports.refundTxCount", { count: totalRefundCount })}</div>
        </div>
        {/* Net Revenue */}
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB", padding: "18px 20px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 8 }}>{t("reports.cardNetRevenue").toUpperCase()}</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: T.textDark }}>
            {formatMYR(totalNet)}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{t("reports.afterRefunds")}</div>
          {totalCancelledRefundCount > 0 && (
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>
              {t("reports.refundsOnCancelled", { count: totalCancelledRefundCount, amount: totalCancelledRefunds.toFixed(2) })}
            </div>
          )}
        </div>
        {/* Total Orders */}
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB", padding: "18px 20px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 8 }}>{t("reports.cardTotalOrders").toUpperCase()}</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: T.textDark }}>
            {period === "today" && dashStats?.data ? dashStats.data.todayOrders : totalOrders}
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{t("reports.pickupDelivery")}</div>
        </div>
      </div>

      {/* Sales Trend + Payment Methods */}
      <div className="admin-report-chart-grid" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 16, marginBottom: 18 }}>
        <SectionCard>
          <SectionTitle right={
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              <span style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: T.brand, display: "inline-block" }} /> {t("reports.legendRevenue")}
              </span>
              <span style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: "#6EE7B7", display: "inline-block" }} /> {t("reports.legendOrders")}
              </span>
            </div>
          }>
            {t("reports.salesTrend")}
          </SectionTitle>
          {revLoading || chartRows.length === 0 ? (
            <div style={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>
              {revLoading ? t("common.loading") : t("reports.chartNoData")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={chartRows} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={T.surfaceSection} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: T.textMuted }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip formatter={(v: unknown, n: unknown) => [n === "revenue" ? formatMYR(v) : String(v), n === "revenue" ? t("reports.legendRevenue") : t("reports.legendOrders")]} />
                <Bar dataKey="revenue" fill={T.brand} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </SectionCard>

        <SectionCard className="w-full md:w-[260px]" style={{ padding: "20px 20px" }}>
          <SectionTitle>{t("reports.paymentMethods")}</SectionTitle>
          {pmLoading || (paymentMethodsData?.data ?? []).length === 0 ? (
            <div style={{ padding: "20px 0", textAlign: "center", color: T.textMuted, fontSize: 12 }}>
              {pmLoading ? t("common.loading") : t("reports.chartNoData")}
            </div>
          ) : (paymentMethodsData?.data ?? []).map((pm, i) => (
            <div key={pm.method} style={{ marginBottom: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: T.textBody }}>{PAY_METHOD_LABEL[pm.method] ?? pm.method.toUpperCase()}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: T.textDark }}>{formatMYR(parseFloat(pm.amount))}</span>
              </div>
              <Progress percent={pm.pct} showInfo={false} strokeColor={DONUT_COLORS[i % DONUT_COLORS.length]} trailColor={T.surfaceSection} size="small" style={{ margin: 0 }} />
            </div>
          ))}
        </SectionCard>
      </div>

      {/* Revenue trend (line chart) */}
      <SectionCard style={{ marginBottom: 18 }}>
        <div style={{ marginBottom: 4, display: "flex", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.textDark }}>{t("reports.revenueTrend")}</div>
            <div style={{ fontSize: 12, color: T.textMuted }}>{t("reports.revenueTrendSub")}</div>
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <span style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 2, background: T.brand, display: "inline-block" }} /> {t("reports.legendRevenue")}
            </span>
            <span style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ width: 10, height: 2, background: T.dangerText, display: "inline-block", borderTop: "2px dashed #DC2626" }} /> {t("reports.legendRefunds")}
            </span>
          </div>
        </div>
        {revLoading || chartRows.length === 0 ? (
          <div style={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>
            {revLoading ? t("common.loading") : t("reports.chartNoData")}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={160}>
            <LineChart data={chartRows} margin={{ top: 10, right: 20, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={T.surfaceSection} vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: T.textMuted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: T.textMuted }} tickFormatter={v => formatMYR(v)} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v: unknown, n: unknown) => [n === "revenue" ? formatMYR(v) : formatMYR(v), n === "revenue" ? t("reports.legendRevenue") : t("reports.legendRefunds")]} />
              <Line type="monotone" dataKey="revenue" stroke={T.brand} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </SectionCard>

      {/* Daily breakdown table */}
      <SectionCard style={{ marginBottom: 18 }}>
        <SectionTitle right={<ExportBtn onClick={() => exportRowsToCsv("daily-breakdown", chartRows.map(r => ({ Date: r.label, Revenue: r.revenue.toFixed(2), Orders: r.orders, Refunds: r.refunds.toFixed(2), RefundsOnCancelledOrders: r.cancelledRefunds.toFixed(2), NetRevenue: r.net.toFixed(2) })))} />}>{t("reports.dailyBreakdown")}</SectionTitle>
        <Table
          size="small"
          rowKey="label"
          loading={revLoading}
          dataSource={chartRows}
          columns={[
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colDate").toUpperCase()}</span>,
              dataIndex: "label", key: "date",
              render: (v: string, _: unknown, i: number) => (
                <span style={{ fontWeight: i === 0 ? 700 : 400, color: T.textDark }}>{v}</span>
              ),
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colRevenue").toUpperCase()}</span>,
              dataIndex: "revenue", key: "rev", align: "right",
              render: (v: number) => <span style={{ fontWeight: 600 }}>{formatMYR(v)}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colRefunds").toUpperCase()}</span>,
              dataIndex: "refunds", key: "refunds", align: "right",
              render: (v: number) => <span style={{ color: T.dangerText, fontWeight: 600 }}>{formatMYR(v)}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colCancelledRefunds").toUpperCase()}</span>,
              dataIndex: "cancelledRefunds", key: "cancelledRefunds", align: "right",
              render: (v: number) => <span style={{ color: T.textMuted }}>{formatMYR(v)}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colNetRevenue").toUpperCase()}</span>,
              dataIndex: "net", key: "net", align: "right",
              render: (v: number) => <span style={{ fontWeight: 700, color: T.textDark }}>{formatMYR(v)}</span>,
            },
          ]}
        />
      </SectionCard>

      {/* Recent financial transactions */}
      <SectionCard>
        <SectionTitle right={
          <Button type="link" style={{ color: T.brand, fontWeight: 700, padding: 0 }} onClick={() => navigate("/transactions")}>{t("reports.viewAll")}</Button>
        }>
          {t("reports.recentFinancialTx")}
        </SectionTitle>
        <Table<Transaction>
          size="small"
          rowKey="id"
          loading={txnLoading}
          dataSource={txnData?.data ?? []}
          columns={txnColumns}
        />
      </SectionCard>
    </div>
  );
}

/* ─── Risk Tab ───────────────────────────────────────────── */
function RiskTab({ params }: { params: { from: string; to: string } }) {
  const { t, i18n } = useTranslation();
  const { data } = useHighRiskOrders({ from: params.from, to: params.to });
  const highRisk = useMemo(
    () => (data?.data.highRisk ?? []).map(r => ({ ...r, customer: !r.customer || r.customer === "Guest" ? t("common.guest") : r.customer, amount: Number(r.amount), reason: cancelReasonLabel(r.reason, i18n.language) || "—" })),
    [data, i18n.language, t],
  );
  const stats = data?.data.stats;
  // Counts come from the server over every matching order — the table below only lists them.
  const cancelledCount = stats?.cancelledCount ?? 0;
  const refundedCount  = stats?.refundedCount ?? 0;
  const totalHigh      = cancelledCount;

  const statusMap: Record<string, { label: string; color: string; bg: string }> = useMemo(() => ({
    cancelled: { label: t("reports.statusCancelled").toUpperCase(), color: T.dangerText, bg: T.dangerBg },
    refunded:  { label: t("reports.statusRefunded").toUpperCase(),  color: T.brandAmberText, bg: T.warningBg },
    refund_pending: { label: t("reports.statusRefundPending").toUpperCase(), color: T.brandAmberText, bg: T.warningBg },
  }), [t]);

  return (
    <div className="admin-report-tab admin-report-risk-tab">
      {/* Stat cards */}
      <div className="admin-report-kpis" style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB", borderTop: "3px solid #F97316", padding: "18px 20px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 8 }}>{t("reports.riskCancelRate").toUpperCase()}</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: T.brand, display: "flex", alignItems: "center", gap: 6 }}>
            {stats ? `${stats.cancelRate}%` : "—"} <ArrowUpOutlined style={{ fontSize: 16, color: T.dangerText }} />
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{t("reports.cancelledOrdersCount", { count: cancelledCount })}</div>
        </div>
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB", borderTop: "3px solid #FBBF24", padding: "18px 20px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 8 }}>{t("reports.riskRefundRate").toUpperCase()}</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: T.brandChartA, display: "flex", alignItems: "center", gap: 6 }}>
            {stats ? `${stats.refundRate}%` : "—"} <ArrowUpOutlined style={{ fontSize: 16, color: T.dangerText }} />
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{t("reports.refundedOrdersCount", { count: refundedCount })}</div>
        </div>
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB", padding: "18px 20px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 8 }}>{t("reports.riskCancelledOrders").toUpperCase()}</div>
          <div style={{ fontSize: 30, fontWeight: 900, color: T.textDark }}>{totalHigh}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{t("reports.thisPeriod")}</div>
        </div>
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 16, border: "1px solid #E5E7EB", padding: "18px 20px", boxShadow: "0 12px 28px rgba(15,23,42,0.04)" }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1, color: T.textMuted, marginBottom: 8 }}>{t("reports.riskRefundedAmt").toUpperCase()}</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: T.brand }}>{stats ? formatMYR(stats.refundedAmount) : "—"}</div>
          <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>{t("reports.totalRefundedHint")}</div>
        </div>
      </div>

      {/* High-risk orders table */}
      <SectionCard>
        <SectionTitle right={<ExportBtn onClick={() => exportRowsToCsv("high-risk-orders", highRisk.map(r => ({ OrderId: r.id, Customer: r.customer, Amount: r.amount.toFixed(2), Type: r.type === "delivery" ? t("receipt.channelDelivery") : t("receipt.channelPickup"), Reason: r.reason, Status: statusMap[r.status]?.label ?? r.status })))} />}>{t("reports.highRiskOrders")}</SectionTitle>
        <Table
          size="small"
          rowKey="id"
          dataSource={highRisk}
          pagination={{
            pageSize: 10,
            showTotal: (total, range) => t("reports.paginationShowing", { start: range[0], end: range[1], total }),
          }}
          columns={[
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colOrderId").toUpperCase()}</span>,
              dataIndex: "id", key: "id", width: 100,
              render: (v: string) => <span style={{ fontFamily: "monospace", fontWeight: 700, color: T.textDark }}>{v}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colCustomerName").toUpperCase()}</span>,
              dataIndex: "customer", key: "customer", width: 140,
              render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colAmount").toUpperCase()}</span>,
              dataIndex: "amount", key: "amount", width: 100, align: "right",
              render: (v: number) => <span style={{ fontWeight: 700 }}>{formatMYR(v)}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colOrderType").toUpperCase()}</span>,
              dataIndex: "type", key: "type", width: 110,
              render: (v: string) => (
                <span style={{
                  padding: "3px 10px", borderRadius: 100, fontSize: 12, fontWeight: 600,
                  background: v === "delivery" ? T.infoBg : T.surfaceSection,
                  color:      v === "delivery" ? T.infoDeep : T.siderHover,
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}>
                  {v === "delivery" ? <CarOutlined /> : <ShoppingOutlined />} {v === "delivery" ? t("receipt.channelDelivery") : t("receipt.channelPickup")}
                </span>
              ),
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colReason").toUpperCase()}</span>,
              dataIndex: "reason", key: "reason",
              render: (v: string) => <span style={{ fontSize: 12, color: T.textBody }}>{v}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colStatus").toUpperCase()}</span>,
              dataIndex: "status", key: "status", width: 110,
              render: (v: string) => {
                const s = statusMap[v] ?? { label: v.toUpperCase(), color: T.siderHover, bg: T.surfaceSection };
                return (
                  <span style={{ padding: "3px 10px", borderRadius: 100, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700 }}>
                    {s.label}
                  </span>
                );
              },
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colActions").toUpperCase()}</span>,
              key: "actions", width: 70, align: "center",
              render: () => <Button type="text" icon={<EyeOutlined />} style={{ color: T.textBody }} />,
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}

/* ─── Sales by Stall Tab ─────────────────────────────────── */
function SalesByStallTab({ params }: { params: { from: string; to: string } }) {
  const { t } = useTranslation();
  const [itemsCategory, setItemsCategory] = useState<"walkin" | "customer" | undefined>("walkin");
  const { data: topItemsData } = useTopItemsByStall({ ...params, category: itemsCategory });
  const byStall = topItemsData?.data ?? {};
  const { data: stallData, isLoading } = useStallBreakdown(params);
  const rows = stallData?.data ?? [];
  const [activeStall, setActiveStall] = useState<string | null>(null);

  const topStall = useMemo(() => {
    if (!rows.length) return null;
    return [...rows].sort((a, b) => parseFloat(b.revenue) - parseFloat(a.revenue))[0];
  }, [rows]);

  const selectedStall = activeStall ?? (topStall?.stallName ?? null);
  const topItems = (selectedStall ? byStall[selectedStall] : undefined)
    ?.map(r => ({ ...r, revenue: Number(r.revenue) })) ?? [];


  const totalRevenue = rows.reduce((s, r) => s + parseFloat(r.revenue), 0);
  const donutData = rows.map(r => ({
    name: r.stallName,
    value: Math.round((parseFloat(r.revenue) / totalRevenue) * 100),
  }));

  return (
    <div className="admin-report-tab admin-report-stalls-tab">
      {/* Sales chart + Donut */}
      <div className="admin-report-chart-grid admin-report-chart-grid-even" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
        <SectionCard>
          <div style={{ marginBottom: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: T.textDark }}>{t("reports.salesByStallTitle")}</div>
              <ExportBtn onClick={() => exportRowsToCsv("sales-by-stall", rows.map(r => ({ Stall: r.stallName, Revenue: Number(r.revenue).toFixed(2) })))} />
            </div>
            <div style={{ fontSize: 12, color: T.textMuted }}>{t("reports.salesByStallSub")}</div>
          </div>
          {isLoading || rows.length === 0 ? (
            <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>
              {isLoading ? t("common.loading") : t("reports.chartNoDataShort")}
            </div>
          ) : (
            rows.map((r, i) => {
              const rev = parseFloat(r.revenue);
              const maxRev = Math.max(...rows.map(x => parseFloat(x.revenue)));
              const pct = maxRev > 0 ? (rev / maxRev) * 100 : 0;
              const barColor = DONUT_COLORS[i % DONUT_COLORS.length] ?? T.brand;
              return (
                <div key={r.stallName} style={{ marginBottom: 16 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: T.textDark, letterSpacing: 0.5 }}>{r.stallName.toUpperCase()}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{formatMYR(parseFloat(r.revenue))}</span>
                  </div>
                  <Progress percent={pct} showInfo={false} strokeColor={barColor} trailColor={T.surfaceSection} style={{ margin: 0 }} />
                </div>
              );
            })
          )}
        </SectionCard>

        <SectionCard>
          <SectionTitle>{t("reports.revenueShare")}</SectionTitle>
          {isLoading || rows.length === 0 ? (
            <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: T.textMuted }}>
              {isLoading ? t("common.loading") : t("reports.chartNoDataShort")}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div className="w-[140px] h-[140px] md:w-[180px] md:h-[180px]" style={{ position: "relative", marginBottom: 16 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} cx="50%" cy="50%" innerRadius={55} outerRadius={85}
                      paddingAngle={2} dataKey="value">
                      {donutData.map((_, i) => (
                        <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length] ?? T.brand} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div style={{
                  position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                }}>
                  <span style={{ fontSize: 22, fontWeight: 900, color: T.textDark }}>100%</span>
                  <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 600 }}>{t("reports.donutTotal").toUpperCase()}</span>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px", width: "100%" }}>
                {donutData.map((d, i) => (
                  <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: DONUT_COLORS[i % DONUT_COLORS.length] ?? T.brand, flexShrink: 0 }} />
                    <span style={{ color: T.textBody }}>{d.name.split(" ")[0]}</span>
                    <span style={{ fontWeight: 700, marginLeft: "auto" }}>{d.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SectionCard>
      </div>

      {/* Top items by stall */}
      <SectionCard>
        <SectionTitle right={<ExportBtn onClick={() => exportRowsToCsv("top-items-by-stall", topItems.map(r => ({
          Rank: r.rank,
          Item: r.name,
          UnitsSold: r.units,
          RevenueRM: (r.netRevenue != null ? Number(r.netRevenue) : r.revenue).toFixed(2),
        })))} />}>
          <div>
            <div>{t("reports.topItemsTitle")}</div>
            <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 400, marginTop: 2 }}>{t("reports.topItemsSub")}</div>
          </div>
        </SectionTitle>
        {/* Order category filter — walk-in vs customer-app, since revenue blends the two by default */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          {([
            { key: "walkin" as const, label: t("reports.categoryWalkin") },
            { key: "customer" as const, label: t("reports.categoryCustomerApp") },
          ]).map(opt => (
            <button key={opt.label} type="button"
              onClick={() => setItemsCategory(opt.key)}
              style={{
                padding: "6px 14px", borderRadius: 100,
                border: itemsCategory === opt.key ? "1.5px solid #ea580c" : "1px solid #E5E7EB",
                cursor: "pointer",
                background: itemsCategory === opt.key ? T.brandLight : T.surface,
                color:      itemsCategory === opt.key ? T.brand : T.textBody,
                fontSize: 12, fontWeight: 700,
              }}>
              {opt.label}
            </button>
          ))}
        </div>
        {/* Stall selector tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
          {rows.map(r => (
            <button key={r.stallName} type="button"
              onClick={() => setActiveStall(r.stallName)}
              style={{
                padding: "7px 16px", borderRadius: 100,
                border: "none", cursor: "pointer",
                background: selectedStall === r.stallName ? T.brand : T.surfaceSection,
                color:      selectedStall === r.stallName ? T.surface    : T.textBody,
                fontSize: 13, fontWeight: 700,
              }}>
              {r.stallName}
            </button>
          ))}
        </div>
        <Table
          size="small"
          rowKey="rank"
          dataSource={topItems}
          columns={[
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colRank").toUpperCase()}</span>,
              dataIndex: "rank", key: "rank", width: 60,
              render: (v: number) => <span style={{ fontWeight: 700, color: T.textMuted }}>{String(v).padStart(2, "0")}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colItemName").toUpperCase()}</span>,
              dataIndex: "name", key: "name",
              render: (v: string) => <span style={{ fontWeight: 600, color: T.textDark }}>{v}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colUnitsSold").toUpperCase()}</span>,
              dataIndex: "units", key: "units", width: 110, align: "right",
              render: (v: number) => <span style={{ fontWeight: 600 }}>{v.toLocaleString()}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colRevenueRm").toUpperCase()}</span>,
              dataIndex: "revenue", key: "revenue", width: 130, align: "right",
              render: (v: number) => <span style={{ fontWeight: 700, color: T.textDark }}>{formatMYR(v)}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colPerformance").toUpperCase()}</span>,
              dataIndex: "pct", key: "pct", width: 160,
              render: (v: number) => (
                <Progress percent={v} showInfo={false} strokeColor={T.brand} trailColor={T.surfaceSection} style={{ margin: 0 }} />
              ),
            },
          ]}
        />
      </SectionCard>
    </div>
  );
}

/* ─── Pricing Breakdown Tab ──────────────────────────────── */
function PricingBreakdownTab() {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => dayjs().format("YYYY-MM"));
  const [stallId, setStallId] = useState<string | undefined>(undefined);
  const [category, setCategory] = useState<"walkin" | "customer" | undefined>(undefined);

  const { data: stallsData } = useAdminStalls();
  const stalls = stallsData?.data ?? [];

  const { data, isLoading } = usePricingBreakdown({ month, stallId, category });
  const rows = data?.data?.stallBreakdown ?? [];
  const totals = data?.data?.totals;

  const columns: ColumnsType<(typeof rows)[number]> = [
    { title: t("reports.colStall"), dataIndex: "name", key: "name" },
    { title: t("reports.colOrders"), dataIndex: "orderCount", key: "orderCount", align: "right" },
    { title: t("reports.colQty"), dataIndex: "qty", key: "qty", align: "right" },
    {
      title: t("reports.colBasePriceTotal"), dataIndex: "basePriceTotal", key: "base", align: "right",
      render: (v: string) => formatMYR(v),
    },
    {
      title: t("reports.colMarkupTotal"), dataIndex: "markupTotal", key: "markup", align: "right",
      render: (v: string) => formatMYR(v),
    },
    {
      title: t("reports.colOptionsTotal"), dataIndex: "optionsTotal", key: "optionsTotal", align: "right",
      render: (v: string) => formatMYR(v),
    },
    {
      title: t("reports.colOptionsMarkupTotal"), dataIndex: "optionsMarkupTotal", key: "optionsMarkup", align: "right",
      render: (v: string) => formatMYR(v),
    },
    {
      title: t("reports.colBundleDiscount"), dataIndex: "bundleDiscountTotal", key: "bundleDiscount", align: "right",
      render: (v?: string) => (Number(v ?? 0) > 0 ? `- ${formatMYR(Number(v))}` : formatMYR(0)),
    },
    {
      title: t("reports.colStallDeduction"), dataIndex: "stallDeductionTotal", key: "stallDeduction", align: "right",
      render: (v?: string) => formatMYR(v ?? 0),
    },
    {
      title: t("reports.colPlatformIncome"), dataIndex: "platformIncomeTotal", key: "platformIncome", align: "right",
      render: (v?: string) => <strong>{formatMYR(Number(v ?? 0))}</strong>,
    },
    {
      title: t("reports.colSstIncluded"), dataIndex: "sstTotal", key: "sstTotal", align: "right",
      render: (v?: string) => formatMYR(v ?? 0),
    },
    {
      title: t("reports.colRevenue"), dataIndex: "revenue", key: "revenue", align: "right",
      render: (v: string) => <strong>{formatMYR(Number(v))}</strong>,
    },
  ];

  return (
    <div className="admin-report-tab admin-report-pricing-tab">
      <SectionCard>
        <SectionTitle
          right={
            <ExportBtn onClick={() => exportRowsToCsv(
              `pricing-breakdown-${month}`,
              [
                ...rows.map(r => ({
                  Stall: r.name, Orders: r.orderCount, Qty: r.qty,
                  BasePriceTotalRM: r.basePriceTotal, MarkupTotalRM: r.markupTotal, BundleDiscountRM: r.bundleDiscountTotal ?? "0.00", StallItemDeductionRM: r.stallFeeTotal ?? "0.00", StallAddonDeductionRM: r.stallAddonFeeTotal ?? "0.00", StallBundleDeductionRM: r.stallBundleFeeTotal ?? "0.00", StallDeductionRM: r.stallDeductionTotal ?? "0.00", PlatformIncomeRM: r.platformIncomeTotal ?? "0.00",
                  OptionsTotalRM: r.optionsTotal, OptionsMarkupTotalRM: r.optionsMarkupTotal, RevenueRM: r.revenue,
                })),
                ...(totals ? [{
                  Stall: t("reports.rowGrandTotal"), Orders: totals.orderCount, Qty: totals.qty,
                  BasePriceTotalRM: totals.basePriceTotal, MarkupTotalRM: totals.markupTotal, BundleDiscountRM: totals.bundleDiscountTotal ?? "0.00", StallItemDeductionRM: totals.stallFeeTotal ?? "0.00", StallAddonDeductionRM: totals.stallAddonFeeTotal ?? "0.00", StallBundleDeductionRM: totals.stallBundleFeeTotal ?? "0.00", StallDeductionRM: totals.stallDeductionTotal ?? "0.00", PlatformIncomeRM: totals.platformIncomeTotal ?? "0.00",
                  OptionsTotalRM: totals.optionsTotal, OptionsMarkupTotalRM: totals.optionsMarkupTotal, RevenueRM: totals.revenue,
                }] : []),
              ],
            )} />
          }
        >
          {t("reports.pricingBreakdownTitle")}
        </SectionTitle>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 16 }}>{t("reports.pricingBreakdownSub")}</div>

        <div className="admin-report-pricing-filters admin-txn-filter-panel" style={{ display: "flex", gap: 12, marginBottom: 18, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, marginBottom: 4 }}>{t("reports.filterMonth")}</div>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={{ height: 40, borderRadius: 10, border: "1px solid #e7e1db", padding: "0 12px", fontSize: 13, fontWeight: 600 }}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, marginBottom: 4 }}>{t("reports.filterStall")}</div>
            <Select
              value={stallId ?? ""}
              onChange={(v: string) => setStallId(v || undefined)}
              style={{ height: 40 }}
              options={[
                { value: "", label: t("reports.filterAllStalls") },
                ...stalls.map((s: { id: string; name: string }) => ({ value: s.id, label: s.name })),
              ]}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, marginBottom: 4 }}>{t("reports.filterCategory")}</div>
            <Select
              value={category ?? ""}
              onChange={(v: string) => setCategory((v || undefined) as "walkin" | "customer" | undefined)}
              style={{ height: 40 }}
              options={[
                { value: "", label: t("reports.categoryAll") },
                { value: "walkin", label: t("reports.categoryWalkin") },
                { value: "customer", label: t("reports.categoryCustomerApp") },
              ]}
            />
          </div>
        </div>

        {totals && (
          <div className="admin-report-pricing-stats" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 18 }}>
            {[
              { label: t("reports.colOrders"), value: totals.orderCount.toLocaleString() },
              { label: t("reports.colBasePriceTotal"), value: formatMYR(totals.basePriceTotal) },
              { label: t("reports.colMarkupTotal"), value: formatMYR(totals.markupTotal) },
              { label: t("reports.colOptionsTotal"), value: formatMYR(totals.optionsTotal) },
              { label: t("reports.colOptionsMarkupTotal"), value: formatMYR(totals.optionsMarkupTotal) },
              { label: t("reports.colStallDeduction"), value: formatMYR(totals.stallDeductionTotal ?? 0) },
              { label: t("reports.colPlatformIncome"), value: formatMYR(totals.platformIncomeTotal ?? 0) },
              { label: t("reports.colSstIncluded"), value: formatMYR(totals.sstTotal ?? 0) },
              { label: t("reports.colRevenue"), value: formatMYR(totals.revenue) },
            ].map(stat => (
              <div key={stat.label} style={{ background: T.surfaceSection, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, marginBottom: 4 }}>{stat.label}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: T.textDark }}>{stat.value}</div>
              </div>
            ))}
          </div>
        )}

        <Table
          columns={columns}
          dataSource={rows}
          rowKey="stallId"
          loading={isLoading}
          pagination={false}
        />
      </SectionCard>
    </div>
  );
}

/* ─── Staff Activity Tab ─────────────────────────────────── */
function StaffActivityTab({ params }: { params: { from: string; to: string } }) {
  const { t, i18n } = useTranslation();
  const { data: sessionsData, isLoading: sessionsLoading } = useStaffSessionsReport({ from: params.from, to: params.to });
  const { data: staffData } = useAdminStaff();
  const [search, setSearch] = useState("");

  const staffSessions: StaffSessionRow[] = useMemo(() => (sessionsData?.data.rows ?? []).map(s => ({
    id: s.id,
    staffId: s.staff_id,
    name: s.name,
    initials: getStaffInitials(s.name),
    color: getStaffAvatarColor(s.name),
    device: s.device_label ?? "—",
    loginAt: s.login_at,
    logoutAt: s.logout_at ?? null,
    loginTime: `${formatMonthDay(s.login_at, i18n.language)}, ${formatOrderTime(s.login_at, i18n.language)}`,
    logoutTime: s.logout_at ? `${formatMonthDay(s.logout_at, i18n.language)}, ${formatOrderTime(s.logout_at, i18n.language)}` : null,
    durationMinutes: s.duration_minutes,
    ongoing: s.status === "online",
    status: s.status,
  })), [sessionsData, i18n.language]);

  // People, not sessions: one staff member with several open sessions (repeat logins, tablets never signed out) is still one person.
  const onlineCount = sessionsData?.data.onlineStaff ?? 0;
  const registeredStaffCount = staffData?.data.length ?? 0;

  const completedDurations = staffSessions.filter(s => s.durationMinutes != null).map(s => s.durationMinutes!);
  const avgSessionDuration = completedDurations.length
    ? formatSessionDuration(Math.round(completedDurations.reduce((a, b) => a + b, 0) / completedDurations.length))
    : "—";

  const filtered = useMemo(() => {
    if (!search) return staffSessions;
    const q = search.toLowerCase();
    return staffSessions.filter(s => s.name.toLowerCase().includes(q));
  }, [search, staffSessions]);

  const statusLabel: Record<string, { label: string; color: string; bg: string }> = useMemo(() => ({
    online:    { label: t("reports.staffOnline").toUpperCase(),    color: T.surface,       bg: T.successText },
    completed: { label: t("reports.staffLoggedOut").toUpperCase(), color: T.textSecondary, bg: T.surfaceSection },
  }), [t]);

  return (
    <div className="admin-report-tab admin-report-staff-tab">
      {/* Stat cards */}
      <div className="admin-report-kpis admin-report-staff-kpis" style={{ display: "flex", gap: 14, marginBottom: 20 }}>
        {/* Active staff */}
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 14, border: "1px solid #F3F4F6", padding: "18px 20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 24, color: T.brand }}><TeamOutlined /></span>
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4, fontWeight: 600 }}>{t("reports.staffActiveToday")}</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4, marginBottom: 8 }}>
            <span style={{ fontSize: 32, fontWeight: 900, color: T.textDark }}>{onlineCount}</span>
            <span style={{ fontSize: 13, color: T.textMuted }}>{t("reports.staffRegisteredOf", { count: registeredStaffCount })}</span>
          </div>
          <Progress percent={registeredStaffCount ? (onlineCount / registeredStaffCount) * 100 : 0} showInfo={false} strokeColor={T.brand} trailColor={T.surfaceSection} style={{ margin: 0 }} />
        </div>
        {/* Avg session */}
        <div style={{ flex: 1, minWidth: 220, background: T.successBright, borderRadius: 14, padding: "18px 20px" }}>
          <div style={{ marginBottom: 8 }}>
            <ClockCircleOutlined style={{ fontSize: 24, color: T.surface }} />
          </div>
          <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)", marginBottom: 4, fontWeight: 600 }}>{t("reports.avgSessionDuration")}</div>
          <div style={{ fontSize: 36, fontWeight: 900, color: T.surface }}>{avgSessionDuration}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 4 }}>⏱ {t("reports.perStaffToday")}</div>
        </div>
        {/* Login sessions */}
        <div style={{ flex: 1, minWidth: 220, background: T.surface, borderRadius: 14, border: "1px solid #F3F4F6", padding: "18px 20px" }}>
          <div style={{ marginBottom: 8 }}>
            <LoginOutlined style={{ fontSize: 24, color: T.textBody }} />
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 4, fontWeight: 600 }}>{t("reports.totalLoginSessions")}</div>
          <div style={{ fontSize: 36, fontWeight: 900, color: T.textDark }}>{sessionsData?.data.total ?? 0}</div>
          <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>🔁 {t("reports.includingRelogins")}</div>
        </div>
      </div>

      {/* Login/logout log */}
      <SectionCard>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.textDark }}>{t("reports.staffLoginLogTitle")}</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{t("reports.staffLoginLogSub")}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Input
              prefix={<SearchOutlined style={{ color: T.textMuted }} />}
              placeholder={t("reports.searchStaffPh")}
              value={search} onChange={e => setSearch(e.target.value)} allowClear
              style={{ width: "100%", maxWidth: 200, height: 36, borderRadius: 8 }}
            />
            <ExportBtn onClick={() => exportRowsToCsv("staff-login-log", filtered.map(r => ({
              Staff: r.name, Device: r.device, LoginAt: formatMytStamp(r.loginAt), Logout: r.logoutAt ? formatMytStamp(r.logoutAt) : "", DurationMin: r.durationMinutes ?? "", Status: r.status,
            })))} />
          </div>
        </div>
        <Table
          size="small"
          loading={sessionsLoading}
          rowKey="id"
          dataSource={filtered}
          columns={[
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colStaffName").toUpperCase()}</span>,
              dataIndex: "name", key: "name", width: 160,
              render: (v: string, row: StaffSessionRow) => (
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Avatar size={34} style={{ background: row.color, fontSize: 12, fontWeight: 800, flexShrink: 0 }}>
                    {row.initials}
                  </Avatar>
                  <span style={{ fontWeight: 700, color: T.textDark }}>{v}</span>
                </div>
              ),
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colDevice").toUpperCase()}</span>,
              dataIndex: "device", key: "device", width: 90,
              render: (v: string) => <span style={{ fontSize: 13, color: T.textBody }}>{v}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colLoginTime").toUpperCase()}</span>,
              dataIndex: "loginTime", key: "login", width: 130,
              render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colLogoutTime").toUpperCase()}</span>,
              dataIndex: "logoutTime", key: "logout", width: 140,
              render: (v: string | null) => <span style={{ color: v == null ? T.borderLight : T.textDark }}>{v ?? "—"}</span>,
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colDuration").toUpperCase()}</span>,
              key: "duration", width: 120,
              render: (_: unknown, row: StaffSessionRow) => {
                const minutes = row.durationMinutes ?? Math.max(0, dayjs().diff(dayjs(row.loginAt), "minute"));
                return (
                  <div>
                    <div style={{ fontWeight: 700, color: row.ongoing ? T.brand : T.textDark }}>{formatSessionDuration(minutes)}</div>
                    {row.ongoing && <div style={{ fontSize: 10, color: T.textMuted }}>{t("reports.ongoing")}</div>}
                  </div>
                );
              },
            },
            {
              title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("reports.colStatus").toUpperCase()}</span>,
              dataIndex: "status", key: "status", width: 170,
              render: (v: string) => {
                const s = statusLabel[v] ?? { label: v.toUpperCase(), color: T.siderHover, bg: T.surfaceSection };
                return (
                  <span style={{
                    padding: "4px 12px", borderRadius: 100,
                    background: s.bg, color: s.color,
                    fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
                  }}>
                    {s.label}
                  </span>
                );
              },
            },
          ]}
        />
        <div style={{ marginTop: 12, fontSize: 11, color: T.textMuted, fontStyle: "italic" }}>
          {t("reports.sessionFootnote")}
        </div>
      </SectionCard>
    </div>
  );
}

/* ─── Screen ─────────────────────────────────────────────── */
export function ReportsScreen() {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState<ReportTab>("financial");
  const [period, setPeriod]       = useState<Period>("week");

  const range = useMemo<[Dayjs, Dayjs]>(() => periodRange(period), [period]);

  const granularity = period === "today" ? "hourly" : period === "week" ? "daily" : "daily";
  const revenueParams = {
    from: range[0].format("YYYY-MM-DD"),
    to:   range[1].format("YYYY-MM-DD"),
    granularity,
  };
  const stallParams = {
    from: range[0].format("YYYY-MM-DD"),
    to:   range[1].format("YYYY-MM-DD"),
  };

  // Page-level export always exports the revenue summary for the currently
  // selected date range, independent of which tab is active — the
  // tab-scoped Export CSV buttons below export that tab's specific table.
  const { data: pageRevenueData } = useRevenueReport(revenueParams);

  return (
    <AdminShell>
      <div className="admin-reports-page admin-list-page">
        <div className="admin-reports-page-inner">
      {/* Page header */}
      <div className="admin-reports-toolbar">
        <div>
          <h1 className="admin-page-title">{t("reports.title")}</h1>
          <p className="admin-reports-subtitle">{periodLabel(range, i18n.language)}</p>
        </div>
        {/* Every other tab has its own Export CSV beside its table; this one is the Financial tab's revenue summary only. */}
        {activeTab === "financial" && (
        <Button
          icon={<DownloadOutlined />}
          onClick={() => {
            const rows = pageRevenueData?.data ?? [];
            exportRowsToCsv("revenue-summary", [
              ...rows.map(d => ({ Date: d.label, Revenue: d.revenue, Orders: d.orders, Refunds: d.refunds, RefundsOnCancelledOrders: d.cancelledRefunds ?? "0.00", NetRevenue: d.netRevenue })),
              { Date: "TOTAL", Revenue: rows.reduce((s, d) => s + Number(d.revenue), 0).toFixed(2), Orders: rows.reduce((s, d) => s + d.orders, 0), Refunds: rows.reduce((s, d) => s + Number(d.refunds), 0).toFixed(2), RefundsOnCancelledOrders: rows.reduce((s, d) => s + Number(d.cancelledRefunds ?? 0), 0).toFixed(2), NetRevenue: rows.reduce((s, d) => s + Number(d.netRevenue), 0).toFixed(2) },
            ]);
          }}
          className="admin-reports-export"
          style={{ height: 44, borderRadius: 12, fontWeight: 800, borderColor: T.textDark, color: T.textDark }}
        >
          {t("common.exportCsv")}
        </Button>
        )}
      </div>

      {/* Tab bar */}
      <TabBar active={activeTab} onChange={setActiveTab} />

      {/* Period picker */}
      {activeTab !== "settlement" && activeTab !== "pricing" && <PeriodBar period={period} onChange={setPeriod} range={range} />}

      {/* Tab content */}
      {activeTab === "financial" && <FinancialTab params={revenueParams} period={period} />}
      {activeTab === "risk"      && <RiskTab params={stallParams} />}
      {activeTab === "stalls"    && <SalesByStallTab params={stallParams} />}
            {activeTab === "settlement" && <SettlementTab />}
      {activeTab === "staff"     && <StaffActivityTab params={stallParams} />}
      {activeTab === "pricing"   && <PricingBreakdownTab />}
        </div>
      </div>
    </AdminShell>
  );
}
